import React, { useEffect, useRef } from "react";
import { Button } from "@web-monorepo/mds";
import { logSparksEvent } from "@src/utils/sparks/logSparksEvent";
import { SparksCancellationWinbackPaywall } from "./SparksCancellationWinbackPaywall";
import { getSparksCancellationWinbackCheckoutPlans } from "./SparksCancellationWinbackPlans";
import type { SparksCancellationWinbackResolver } from "./SparksCancellationWinbackResolver";
import { useSparksCancellationWinbackController } from "./useSparksCancellationWinbackController";

type Props = {
  winbackId: string;
  email?: string;
  resolver?: SparksCancellationWinbackResolver;
};

export const SparksCancellationWinbackPage = ({ winbackId, email = "", resolver }: Props) => {
  const state = useSparksCancellationWinbackController(winbackId, resolver);
  const viewedResolutionRef = useRef<string>();

  useEffect(() => {
    if (state.status !== "resolved" || viewedResolutionRef.current === state.resolution) return;

    void logSparksEvent({
      eventName: "parent.sparks.winback.paywall.viewed",
      metadata: {
        platform: "web",
        promotion: state.resolution === "eligible" ? "free_month" : "free_week",
        resolution: state.resolution,
        has_winback_id: true,
      },
    });
    viewedResolutionRef.current = state.resolution;
  }, [state]);

  if (state.status === "loading") {
    return (
      <main className="brand-sparks min-h-[60svh] w-full bg-sky-300" aria-busy="true">
        <span className="sr-only">Loading your promotion</span>
      </main>
    );
  }

  if (state.status === "error") {
    return (
      <main className="brand-sparks flex min-h-[60svh] w-full flex-col items-center justify-center bg-sky-300 px-lg text-center text-brand-secondary">
        <h1 className="font-melun text-[36px] font-bold leading-[1.2] lg:text-[56px]">
          We couldn&apos;t load your promotion.
        </h1>
        <p className="mt-md font-ginto text-[18px]">Check your connection and try again.</p>
        <Button className="mt-xl" variant="primary" size="m" onClick={state.retry}>
          Try again
        </Button>
      </main>
    );
  }

  const plans = getSparksCancellationWinbackCheckoutPlans({
    resolution: state.resolution,
    winbackId,
    email,
  });

  return <SparksCancellationWinbackPaywall resolution={state.resolution} plans={plans} />;
};
